import React from 'react';
import PropTypes from 'prop-types';
import { useUserContext } from '@/src/contexts/user-context';
import Button from '@/src/components/Button';
import MoreQuizzes from '../MoreQuizzes';
import s from './QuizStats.module.scss';
import RenderAnimation from '../../../RenderAnimation';
import GoldResultAnimation from '../../images/resultbanner_GOLD.json';
import SilverResultAnimation from '../../images/resultbanner_SILVER.json';
import PurpleResultAnimation from '../../images/resultbanner_PURPLE.json';
import { ReactComponent as GryffindorCrest } from '../../images/gryffindor.svg';
import { ReactComponent as HufflepuffCrest } from '../../images/hufflepuff.svg';
import { ReactComponent as RavenclawCrest } from '../../images/ravenclaw.svg';
import { ReactComponent as SlytherinCrest } from '../../images/slytherin.svg';

const propTypes = {
  totalQuestions: PropTypes.number,
  userScore: PropTypes.number,
  retakeQuiz: PropTypes.func,
  houseScores: PropTypes.arrayOf(
    PropTypes.shape({
      houseName: PropTypes.string,
      houseScore: PropTypes.number,
    }),
  ),
  relatedQuizzes: PropTypes.array,
  isEvent: PropTypes.bool,
};

const QuizStats = ({
  totalQuestions = null,
  userScore = null,
  retakeQuiz = () => {},
  houseScores = [],
  relatedQuizzes = [],
  isEvent = false,
}) => {
  const { profile } = useUserContext();

  const handleRetakeQuiz = () => {
    retakeQuiz();
    window.scrollTo(0, 0);
  };

  const renderHouseCrest = (house) => {
    let crest = null;
    switch (house) {
      case 'gryffindor':
        crest = <GryffindorCrest />;
        break;
      case 'hufflepuff':
        crest = <HufflepuffCrest />;
        break;
      case 'ravenclaw':
        crest = <RavenclawCrest />;
        break;
      case 'slytherin':
        crest = <SlytherinCrest />;
        break;
      /* istanbul ignore next */
      default:
        break;
    }
    return crest;
  };

  const renderAnimationBanner = (score, total) => {
    const percentage = ((score / total) * 100).toFixed();
    let animationBanner;
    if (percentage <= 50) {
      animationBanner = SilverResultAnimation;
    } else if (percentage <= 99) {
      animationBanner = PurpleResultAnimation;
    } else {
      animationBanner = GoldResultAnimation;
    }
    return animationBanner;
  };

  const getRatingText = (score, total) => {
    const percentage = ((score / total) * 100).toFixed();
    let text;
    if (percentage <= 25) {
      text = 'You need to study harder!';
    } else if (percentage <= 50) {
      text = 'Could be worse... but could be better!';
    } else if (percentage <= 75) {
      text = 'A gallant effort. We can expect great things from you.';
    } else if (percentage <= 99) {
      text = 'Hermione would be proud!';
    } else {
      text = 'Impeccable performance!';
    }
    return text;
  };

  const placement = ['1st', '2nd', '3rd', '4th'];

  return (
    <div className={s.root}>
      <div className={[s.borderWrapper, isEvent ? s.isEvent : ''].join(' ')}>
        <div className={s.resultsAndLeaderboard}>
          <div className={[s.results, isEvent ? s.isEvent : ''].join(' ')}>
            <div className={s.resultsTitle}>RESULTS</div>
            <div className={s.scoreBackground}>
              <div className={s.animationWrapper}>
                <RenderAnimation
                  ariaLabel="User Score Banner"
                  animation="ResultAnimation"
                  animationData={renderAnimationBanner(userScore, totalQuestions)}
                  autoplay={false}
                  loop={false}
                  className={s.bannerAnimation}
                />
                <div className={s.userScore}>
                  <div>Score</div>
                  {`${userScore}/${totalQuestions}`}
                </div>
              </div>
            </div>
            <div className={s.rating}>{getRatingText(userScore, totalQuestions)}</div>
            {isEvent && (
              <div className={s.eventContributions}>
                {`You've contributed `}
                <span
                  className={[s.houseColor, s[profile?.hogwartsHouse?.toLowerCase()]].join(' ')}
                >
                  {userScore}
                </span>
                {` ${userScore === 1 ? 'point' : 'points'} to `}
                <span
                  className={[s.houseColor, s[profile?.hogwartsHouse?.toLowerCase()]].join(' ')}
                >
                  {profile?.hogwartsHouse}
                </span>
                {`.`}
              </div>
            )}
          </div>
          {!isEvent && (
            <div className={s.leaderBoard}>
              <div className={s.header}>
                <div className={s.leaderBoardTitle}>HOUSE LEADERBOARD</div>
                {profile?.givenName && profile?.familyName && (
                  <div className={s.personalInfo}>
                    {`${profile.givenName} ${profile.familyName}`}
                  </div>
                )}
              </div>
              <div className={s.houseScores}>
                {houseScores.map((house, index) => {
                  return (
                    <div
                      className={[
                        s.houseWrapper,
                        house.houseName.toLowerCase() === profile?.hogwartsHouse?.toLowerCase()
                          ? s.active
                          : '',
                        s[house.houseName.toLowerCase()],
                      ].join(' ')}
                      key={house.houseName}
                      style={{ animationDelay: `${index * 0.2}s` }}
                    >
                      <div className={s.place}>{placement[index]}</div>
                      <div className={s.houseCrest}>{renderHouseCrest(house.houseName)}</div>
                      <div className={s.scoreWrapper}>
                        <div className={s.scoreDetails}>
                          <div className={s.houseName}>{house.houseName}</div>
                          <div
                            className={s.houseScoreText}
                            data-testid={`${house.houseName}ScoreText`}
                          >
                            <span className={s.actualScore} data-testid={`${house.houseName}Score`}>
                              {parseFloat(house.houseScore?.toFixed(2))}
                            </span>
                            {`/${totalQuestions}`}
                          </div>
                        </div>
                        <div className={s.scoreBar}>
                          <div
                            style={{
                              width: `${((house.houseScore / totalQuestions) * 100).toFixed()}%`,
                              animationDelay: `${index * 0.2 + 0.4}s`,
                            }}
                            className={[s.houseScore, s[house.houseName.toLowerCase()]].join(' ')}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {!isEvent && (
        <>
          <div className={s.buttonWrapper}>
            <Button label="Retake Quiz" tabIndex="-1" onClick={() => handleRetakeQuiz()} />
          </div>
          <MoreQuizzes relatedQuizzes={relatedQuizzes} />
        </>
      )}
    </div>
  );
};

QuizStats.propTypes = propTypes;
export default QuizStats;
